import { useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { Turnstile } from "./Turnstile";
import { ErrorAlert } from "./AuthLayout";
import { LabeledInput, PasswordInput } from "./AuthFields";
import { secureAuth } from "../lib/authApi";
import { appUrl } from "../lib/config";
import { EMAIL_RE } from "../lib/validation";

const LABELS = { login: "Sign in", staff_login: "Sign in to admin", register: "Create account" };

export function EmailAuthForm({ mode, footer, extra }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [token, setToken] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const ts = useRef(null);
  const isRegister = mode === "register";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!EMAIL_RE.test(email.trim())) return setError("Please enter a valid email address.");
    if (password.length < 8) return setError("Password must be at least 8 characters.");
    if (isRegister && password !== confirm) return setError("Passwords do not match.");
    if (!token) return setError("Please complete the security check.");
    setBusy(true);
    try {
      const res = await secureAuth(mode, { email: email.trim(), password, turnstileToken: token, redirectTo: appUrl("/login") });
      if (res.needsConfirmation) setInfo("Account created. Please check your email to confirm your address, then sign in.");
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
      ts.current?.reset();
    }
  };

  return (
    <div>
      <form onSubmit={submit} noValidate className="space-y-4" data-testid={`${mode}-form`}>
        <LabeledInput id="email" label="Email address" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} testId="auth-email-input" />
        <PasswordInput id="password" label="Password" autoComplete={isRegister ? "new-password" : "current-password"} value={password} onChange={(e) => setPassword(e.target.value)} testId="auth-password-input" />
        {isRegister && (
          <PasswordInput id="confirm" label="Confirm password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} testId="auth-confirm-password-input" />
        )}
        {extra}
        <Turnstile ref={ts} onToken={setToken} action={mode.replace("_", "-")} />
        <ErrorAlert message={error} />
        {info && <p data-testid="auth-info-message" className="rounded-lg bg-[#F5E6BA]/60 px-3 py-2.5 text-sm text-[#4A000E]">{info}</p>}
        <Button type="submit" disabled={busy} data-testid="auth-submit-button" className="h-11 w-full rounded-full bg-[#7A0016] font-semibold text-[#F5E6BA] transition-colors hover:bg-[#4A000E]">
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {LABELS[mode]}
        </Button>
      </form>
      {footer && <div className="mt-6 text-center text-sm text-[#8C6A6C]">{footer}</div>}
    </div>
  );
}
